// ─────────────────────────────────────────────────────────────────────────────
// firmwareCrosscheck.js — Compare our pure-JS stroke metrics against NK's
// firmware-computed summary packet.
//
// Tolerances are calibrated to coach-meaningful drift, not numeric equality:
//   • CA / FA / MFA  ±2°    (rounding & threshold differences)
//   • EL              ±3°    (compounds CA+FA noise)
//   • MXF             ±10 N  or 5% — whichever is larger
//   • OPW             ±10 W  or 5% — whichever is larger
//   • SL / WA         ±2°
//
// Verdict ladder:
//   • match   — every comparable field within tolerance
//   • drift   — 1–2 fields outside
//   • diverge — 3+ fields outside (something is wrong with one side)
// ─────────────────────────────────────────────────────────────────────────────

export const CROSSCHECK_TOLERANCES = {
  CA:  { abs: 2,  pct: 0  },
  FA:  { abs: 2,  pct: 0  },
  EL:  { abs: 3,  pct: 0  },
  MFA: { abs: 3,  pct: 0  },
  MXF: { abs: 10, pct: 5  },
  OPW: { abs: 10, pct: 5  },
  SL:  { abs: 2,  pct: 0  },
  WA:  { abs: 2,  pct: 0  },
};

const FIELDS = Object.keys(CROSSCHECK_TOLERANCES);

function isFinite2(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * Compare a single metric. Returns null if either input is missing (we don't
 * count missing fields against either side — NK may not surface every metric).
 */
export function compareField(name, ours, theirs) {
  if (!isFinite2(ours) || !isFinite2(theirs)) return null;
  const tol = CROSSCHECK_TOLERANCES[name] || { abs: 0, pct: 0 };
  const delta = ours - theirs;
  const absDelta = Math.abs(delta);
  const reference = Math.max(Math.abs(theirs), Math.abs(ours), 1e-6);
  const pctDelta = (absDelta / reference) * 100;
  const allowed = Math.max(tol.abs, (tol.pct * reference) / 100);
  return {
    field: name,
    ours,
    theirs,
    delta,
    absDelta,
    pctDelta,
    tolerance: allowed,
    inTolerance: absDelta <= allowed,
  };
}

/**
 * Cross-check our processStroke output against NK's summary. Both inputs are
 * objects keyed by the same metric names. Missing keys are skipped.
 */
export function crossCheckMetrics(ours = {}, theirs = {}) {
  const rows = FIELDS.map(f => compareField(f, ours[f], theirs[f])).filter(Boolean);
  if (rows.length === 0) {
    return { rows: [], compared: 0, mismatches: 0, verdict: 'no_data' };
  }
  const mismatches = rows.filter(r => !r.inTolerance).length;
  let verdict;
  if (mismatches === 0) verdict = 'match';
  else if (mismatches <= 2) verdict = 'drift';
  else verdict = 'diverge';
  return {
    rows,
    compared: rows.length,
    mismatches,
    verdict,
  };
}

export const VERDICT_LABEL = {
  no_data: 'No comparable data',
  match:   'Matches firmware',
  drift:   'Slight drift',
  diverge: 'Diverges — investigate',
};

export const VERDICT_TONE = {
  no_data: 'zinc',
  match:   'emerald',
  drift:   'amber',
  diverge: 'rose',
};
